import DOMPurify from "dompurify";
import linkifyHtml from "linkify-html";
import { Marked } from "marked";
import {
  isHtmlEmailBody,
  splitEmailTrimmedContent,
} from "./splitEmailTrimmedContent";
import { unwrapMachineDump } from "./unwrapMachineDump";

export type EmailBodyHtml = {
  html: string;
  trimmedHtml: string | null;
  isHtmlSource: boolean;
};

// a private instance — the shared `marked` singleton is configured elsewhere
const emailMarkdown = new Marked({
  gfm: true,
  breaks: true,
  // plain-text emails indent freely, so an indented line is never code
  tokenizer: { code: () => undefined },
});

// Gmail encodes bold as *text* in the plain-text part, where markdown would
// read a single asterisk as italic. A space after the opening asterisk means
// it is a bullet, not emphasis.
const GMAIL_BOLD_REGEX = /(^|[^\w*])\*(?![\s*])([^*\n]*[^\s*])\*(?![\w*])/g;

// Gmail inlines a link in the plain-text part as "label\n<URL>" — e.g. a large
// file appears as a filename line followed by its Drive URL
const LABELLED_URL_REGEX = /^([^\n<>]*[^\s<>])\r?\n<(https?:\/\/[^>\s]+)>/gm;

// html-to-text converters render a standalone button as "Take Action ( URL )".
// Only a whole line counts: mid-sentence parentheses give no way to tell how
// much of the sentence was the anchor text.
const CTA_LINE_REGEX =
  /^[ \t]*([^\s([][^(\n]{0,60}?)[ \t]*\([ \t]*(https?:\/\/[^\s)]+)[ \t]*\)[ \t]*\r?$/gm;

// A line of repeated -, =, _ or * right below a line of text reads as a setext
// underline or a rule to markdown, which swallows the text above it into a
// heading and splits the surrounding lines apart. In an email that line is just
// a signature or section separator, so it stays literal.
const DIVIDER_LINE_REGEX = /^[ \t>]*([-=_*])\1*[ \t]*$/;
const DIVIDER_CHAR_REGEX = /[-=_*]/g;

const escapeMarkdownLinkText = (label: string): string =>
  label.replace(/[[\]]/g, "\\$&");

const escapeAdjoiningDividers = (text: string): string => {
  const lines = text.split("\n");

  return lines
    .map((line, index) =>
      index > 0 && lines[index - 1].trim() && DIVIDER_LINE_REGEX.test(line)
        ? line.replace(DIVIDER_CHAR_REGEX, "\\$&")
        : line
    )
    .join("\n");
};

const toMarkdownLink = (_: string, label: string, url: string) =>
  `[${escapeMarkdownLinkText(label)}](${url})`;

const plainTextToMarkdown = (text: string): string =>
  escapeAdjoiningDividers(
    unwrapMachineDump(text)
      .replace(CTA_LINE_REGEX, toMarkdownLink)
      .replace(LABELLED_URL_REGEX, toMarkdownLink)
      .replace(GMAIL_BOLD_REGEX, "$1**$2**")
  );

const withExternalLinks = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.body.querySelectorAll("a[href]").forEach((anchor) => {
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  });

  return doc.body.innerHTML;
};

const toSafeHtml = (html: string): string =>
  withExternalLinks(
    linkifyHtml(DOMPurify.sanitize(html, { FORBID_TAGS: ["style"] }), {
      defaultProtocol: "https",
    })
  );

const renderMarkdownBody = (text: string): string =>
  toSafeHtml(emailMarkdown.parse(plainTextToMarkdown(text), { async: false }));

/**
 * Prepares an email body for rendering inside a chat bubble:
 * - HTML bodies are sanitized as-is (they carry their own formatting)
 * - plain-text bodies are parsed as markdown, so the emphasis, lists and quote
 *   markers providers encode into the text/plain part become real formatting
 * - the quoted history and boilerplate footer are returned separately so they
 *   can be collapsed
 */
export const emailBodyToHtml = (text: string): EmailBodyHtml => {
  const { body, trimmed } = splitEmailTrimmedContent(text);
  const isHtmlSource = isHtmlEmailBody(text);
  const render = isHtmlSource ? toSafeHtml : renderMarkdownBody;

  return {
    html: render(body),
    trimmedHtml: trimmed ? render(trimmed) : null,
    isHtmlSource,
  };
};
